/**
 * Hifz settings — §10.3.
 *
 * Like the audio slice, every setter bumps `audioEpoch` through `edit` — a repeat count
 * or a drill toggle changed mid-verse must be heard on that verse.
 *
 * `Infinity` IS A LEGAL REPEAT VALUE (A20). It survives a reload because of the codec
 * in `./storage.ts`; it survives arithmetic because `clampRepeat` passes it through
 * untouched rather than clamping it to 20.
 */
import { usePlayer } from '../player'
import type { HifzSlice, SettingsEdit } from './types'

/** Repeat counts are whole, at least 1, and `Infinity` passes through untouched. */
export const clampRepeat = (n: number) => {
  if (n === Infinity) return Infinity
  if (!Number.isFinite(n)) return 1
  return Math.max(1, Math.min(20, Math.round(n)))
}

/**
 * The reducer in @mushaf/corpus owns `ayahRepeatsLeft`/`passageRepeatsLeft` and re-arms
 * them from `state.repeat` (A19), so the count has to reach it. THIS SLICE AND THE
 * REHYDRATION HOOK ARE THE ONLY CALLERS OF `setRepeat`: the preference is the single
 * source and the reducer's copy is a mirror of it, in the same sense that `currentKey`
 * mirrors `queue[index]`.
 */
export const pushRepeat = (patch: { perAyah?: number; perPassage?: number }) => {
  usePlayer.getState().setRepeat(patch)
}

export const createHifzSlice = (edit: SettingsEdit): HifzSlice => ({
  repeatAyah: 1,
  repeatPassage: 1,
  hifzMode: false,
  hideText: false,
  quiz: 'none',

  setRepeatAyah: (n) => {
    const repeatAyah = clampRepeat(n)
    pushRepeat({ perAyah: repeatAyah })
    edit({ repeatAyah })
  },
  setRepeatPassage: (n) => {
    const repeatPassage = clampRepeat(n)
    pushRepeat({ perPassage: repeatPassage })
    edit({ repeatPassage })
  },
  setHifzMode: (hifzMode) => edit({ hifzMode }),
  setHideText: (hideText) => edit({ hideText }),
  setQuiz: (quiz) => edit({ quiz }),
})
